import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CardSubtitle, Input } from 'reactstrap';
import { TableFilterDateRange, TableFilterIdentitySource } from '~/components/Filters';
import { UncontrolledTable } from '~/components';
import usePatient from '~/utils/patient/hook';
import { MatchStatus } from '~/utils/const/identity';

import Hearing from './hearing';
import Vision from './vision';

const TableFilterReferencerange = ({
  onChange,
}) => {
  const [value, setValue] = useState();

  const setFilterValue = (e) => {
    setValue(e.target.value);

    if (e && e.target.value.toLowerCase() === 'negative') {
      onChange('null');
    } else {
      onChange(e.target.value);
    }
  };

  return (
    <Input
      value={value}
      onChange={setFilterValue}
      className="w-100"
    />
  );
};

TableFilterReferencerange.propTypes = {
  onChange: PropTypes.func.isRequired,
};

const Screenings = ({ allData }) => {
  const [, { sacwisPersonId: sacwisId }] = usePatient();

  return (
    <React.Fragment>
      <CardSubtitle tag="h5" className="m-3">
        Well Child (Annual Physical)
      </CardSubtitle>

      <UncontrolledTable
        name="screenings"
        className="mx-4"
        showPaginationBottom={false}
        filterable={false}
        endpoint={{
          url: 'data/public/list_view_cs_tbl_visit',
          params: {
            sacwis_id: sacwisId,
            match_status_id: MatchStatus.Approved,
            visittype: 'WCC',
          },
        }}
        defaultSorted={[{ id: 'visitdateTs', desc: true }]}
        columns={[
          {
            id: 'visitdateTs',
            Header: 'Visit Date',
            accessor: 'attributes.visitdateTs',
            cellType: 'date',
            Filter: d => <TableFilterDateRange {...d} />,
          },
          {
            id: 'primarydx',
            Header: 'Qualifying Diagnosis',
            accessor: 'attributes.primarydx',
          },
          {
            id: 'provname',
            Header: 'Provider',
            accessor: 'attributes.provname',
          },
          {
            id: 'location',
            Header: 'Specialty',
            accessor: 'attributes.location',
          },
          {
            id: 'campuslocation',
            Header: 'Campus Location',
            accessor: 'attributes.campuslocation',
          },
          {
            id: 'sourceDescription',
            Header: 'Reporting Healthcare Insitution',
            accessor: 'attributes.sourceDescription',
            Filter: d => <TableFilterIdentitySource {...d} />,
          },
          {
            id: 'additionaldx',
            Header: 'Diagnosis',
            accessor: 'attributes.additionaldx',
          },
        ]}
      />

      <p className="mt-3 text-muted">
        <em>
          **Indicates the most recent date of a well child check or routine
          health evaluation at the hospital affiliated primary care centers.
          This date may not reflect a more recent well child check completed at
          a non-hospital affiliated primary care center, i.e. a community
          provider.
        </em>
      </p>

      <CardSubtitle tag="h5" className="m-3">
        Dental
      </CardSubtitle>

      <UncontrolledTable
        name="dental-screen"
        className="mx-4"
        endpoint={{
          url: 'data/public/list-view-cs-tbl-dentalscreen',
          params: {
            sacwis_id: sacwisId,
            match_status_id: MatchStatus.Approved,
            'all-data': allData ? '1' : null,
          },
        }}
        defaultSorted={[{ id: 'screendateTs', desc: true }]}
        columns={[
          {
            id: 'screendateTs',
            Header: 'Last Dental Screen Date',
            accessor: 'attributes.screendateTs',
            cellType: 'date',
            Filter: d => <TableFilterDateRange {...d} />,
          },
          {
            id: 'sourceDescription',
            Header: 'Reporting Healthcare Insitution',
            accessor: 'attributes.sourceDescription',
            Filter: d => <TableFilterIdentitySource {...d} />,
          },
        ]}
      />

      <p className="mt-3 text-muted">
        <em>
          **Indicates the most recent date of a dental evalulation at the
          hospital affliated dental centers. This date may not reflect a more
          recent dental evaluation completed at a non-hospital affiliated dental
          center. i.e. a community provider.
        </em>
      </p>

      <CardSubtitle tag="h5" className="m-3">
        Vitals
      </CardSubtitle>

      <UncontrolledTable
        name="vitals"
        className="mx-4"
        endpoint={{
          url: 'data/public/list-view-cs-tbl-flowsheetobservations',
          params: {
            sacwis_id: sacwisId,
            match_status_id: MatchStatus.Approved,
            'all-data': '1',
          },
        }}
        showPaginationBottom={false}
        defaultSorted={[{ id: 'flomeasid' }, { id: 'dispname' }, { id: 'recordedtimeTs', desc: true }]}
        columns={[
          {
            id: 'dispname',
            Header: 'Last Height and Weight',
            accessor: 'attributes.dispname',
          },
          {
            id: 'measvalue',
            Header: '',
            accessor: d =>
              `${d.attributes.measvalue} ${d.attributes.unit || ''}`,
          },
          {
            id: 'recordedtimeTs',
            Header: 'Recorded Date',
            accessor: 'attributes.recordedtimeTs',
            cellType: 'date',
            Filter: d => <TableFilterDateRange {...d} />,
          },
          {
            id: 'sourceDescription',
            Header: 'Reporting Healthcare Insitution',
            accessor: 'attributes.sourceDescription',
            Filter: d => <TableFilterIdentitySource {...d} />,
          },
        ]}
      />

      <Hearing allData={allData} />

      <Vision allData={allData} />

      <CardSubtitle tag="h5" className="m-3">
        Lead
      </CardSubtitle>

      <UncontrolledTable
        name="lead"
        className="mx-4"
        endpoint={{
          url: 'data/public/list-view-cs-tbl-labs',
          params: {
            labcategory: 'Lead',
            sacwis_id: sacwisId,
            match_status_id: MatchStatus.Approved,
            'all-data': allData ? '1' : null,
          },
        }}
        defaultSorted={[{ id: 'resulttimeTs', desc: true }]}
        columns={[
          {
            id: 'componentname',
            Header: 'Component',
            accessor: 'attributes.componentname',
          },
          {
            id: 'resulttimeTs',
            Header: 'Result Date',
            cellType: 'date',
            accessor: 'attributes.resulttimeTs',
            Filter: d => <TableFilterDateRange {...d} />,
          },
          {
            id: 'resultvalue',
            Header: 'Result',
            accessor: d =>
              `${d.attributes.resultvalue} ${d.attributes.referenceunit || ''}`,
          },
          {
            id: 'referencerange',
            Header: 'Normal Range',
            accessor: 'attributes.referencerange',
          },
          {
            id: 'ageatresult',
            Header: 'Age At Result',
            accessor: 'attributes.ageatresult',
          },
          {
            id: 'sourceDescription',
            Header: 'Reporting Healthcare Insitution',
            accessor: 'attributes.sourceDescription',
            Filter: d => <TableFilterIdentitySource {...d} />,
          },
        ]}
      />

      <CardSubtitle tag="h5" className="m-3">
        Hemoglobin
      </CardSubtitle>

      <UncontrolledTable
        name="hemoglobin"
        className="mx-4"
        endpoint={{
          url: 'data/public/list-view-cs-tbl-labs',
          params: {
            labcategory: 'Hgb',
            sacwis_id: sacwisId,
            match_status_id: MatchStatus.Approved,
            'all-data': allData ? '1' : null,
          },
        }}
        defaultSorted={[{ id: 'resulttimeTs', desc: true }]}
        columns={[
          {
            id: 'componentname',
            Header: 'Component',
            accessor: 'attributes.componentname',
          },
          {
            id: 'resulttimeTs',
            Header: 'Result Date',
            accessor: 'attributes.resulttimeTs',
            cellType: 'date',
            Filter: d => <TableFilterDateRange {...d} />,
          },
          {
            id: 'resultvalue',
            Header: 'Result',
            accessor: d =>
              `${d.attributes.resultvalue} ${d.attributes.referenceunit || ''}`,
          },
          {
            id: 'referencerange',
            Header: 'Normal Range',
            accessor: 'attributes.referencerange',
          },
          {
            id: 'ageatresult',
            Header: 'Age At Result',
            accessor: 'attributes.ageatresult',
          },
          {
            id: 'sourceDescription',
            Header: 'Reporting Healthcare Insitution',
            accessor: 'attributes.sourceDescription',
            Filter: d => <TableFilterIdentitySource {...d} />,
          },
        ]}
      />

      <CardSubtitle tag="h5" className="m-3">
        Hepatitis C Ab and C Virus Quant
      </CardSubtitle>

      <UncontrolledTable
        name="hepatitis-c-ab-and-c-virus-quant"
        className="mx-4"
        endpoint={{
          url: 'data/public/list-view-cs-tbl-labs',
          params: {
            labcategory: 'Hcv',
            sacwis_id: sacwisId,
            match_status_id: MatchStatus.Approved,
            'all-data': allData ? '1' : null,
          },
        }}
        defaultSorted={[{ id: 'resulttimeTs', desc: true }]}
        columns={[
          {
            id: 'componentname',
            Header: 'Component',
            accessor: 'attributes.componentname',
          },
          {
            id: 'resulttimeTs',
            Header: 'Result Date',
            accessor: 'attributes.resulttimeTs',
            cellType: 'date',
            Filter: d => <TableFilterDateRange {...d} />,
          },
          {
            id: 'resultvalue',
            Header: 'Result',
            accessor: d =>
              `${d.attributes.resultvalue} ${d.attributes.referenceunit || ''}`,
          },
          {
            id: 'referencerange',
            Header: 'Normal Range',
            accessor: 'attributes.referencerange',
            Cell: d => (
              <>
                {d.original.attributes.referencerange || 'Negative'}
              </>
            ),
            Filter: d => <TableFilterReferencerange {...d} />,
          },
          {
            id: 'ageatresult',
            Header: 'Age At Result',
            accessor: 'attributes.ageatresult',
          },
          {
            id: 'sourceDescription',
            Header: 'Reporting Healthcare Insitution',
            accessor: 'attributes.sourceDescription',
            Filter: d => <TableFilterIdentitySource {...d} />,
          },
        ]}
      />

      <p className="mt-3 text-muted">
        <em>
          **A positive or indeterminate test in a children less than 18 months
          of age may represent maternal antibody and in a small percentage may
          represent actual disease or infection
        </em>
      </p>
    </React.Fragment>
  );
};

Screenings.propTypes = {
  allData: PropTypes.bool,
};

Screenings.defaultProps = {
  allData: false,
};

export default Screenings;
